/**
 * Named FullCalendar views, and the helpers that tell them apart.
 *
 * The account views are vertical hour grids: time runs down, calendars or
 * days take the columns. The day Groups view stays a horizontal timeline —
 * not for reading, but for booking: only a resource column can carry the
 * collapsible group hierarchy, and it is the group's own row that turns one
 * drag into an invitation to every member.
 *
 * A week of member rows is the case where that breaks down: seven times the
 * width, and a half-hour appointment a few pixels wide. That view is flat and
 * vertical instead.
 *
 * The names matter: `view.type` carries the registered NAME, which is what
 * `isGroupFcView` keys on.
 */

/** Accounts, one column per calendar, over a day. */
export const PLANNING_DAY_VIEW = 'resourceTimeGridDay';
/** Accounts, one column per calendar, over a week. */
export const PLANNING_WEEK_VIEW = 'resourceTimeGridWeek';
/**
 * Plan groups over a day: groups as collapsible rows, time running right.
 *
 * Horizontal on purpose. The resource column is what holds the hierarchy, and
 * dragging on a group's own row invites every member at once — the fastest
 * way to book a meeting, and the reason this view is not a vertical grid.
 */
export const GROUPS_DAY_VIEW = 'resourceTimelineDay';
/**
 * Plan groups over a week: a plain hour grid, days in columns.
 *
 * Seven days of member columns would not fit, so members are not columns here
 * at all: everyone's events share the day, merged per slot, and each member's
 * colour is what says who (see mergeGroupEvents).
 */
export const GROUPS_WEEK_VIEW = 'groupsTimeGridWeek';

/** True for the one view laid out as resource rows (horizontal time axis). */
export function isTimelineFcView(viewType) {
  return viewType === GROUPS_DAY_VIEW;
}

/** True for the views driven by the plan groups rather than by the accounts. */
export function isGroupFcView(viewType) {
  return viewType === GROUPS_DAY_VIEW || viewType === GROUPS_WEEK_VIEW;
}

/**
 * True where no column carries a member's name, so their colour is the only
 * thing saying whose appointment it is — events there are merged per slot and
 * get a solid fill.
 */
export function isFlatGroupFcView(viewType) {
  return viewType === GROUPS_WEEK_VIEW;
}
